import React, { memo, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Mail, PhoneCall, Video, CreditCard, MessageSquare, CheckSquare, 
  User, Edit, RefreshCw, Ticket as TicketIcon, Handshake as HandshakeIcon,
  Users, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Activity Detail Dialog
export const ActivityDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  activity,
  activityIcons = {}
}) => {
  const Icon = activity ? (activityIcons[activity.activity_type] || MessageSquare) : MessageSquare;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {activity && (
              <>
                <Icon className="h-5 w-5" />
                {activity.title}
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        {activity && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{activity.author?.full_name || activity.author?.email || 'System'}</span>
              <span>•</span>
              <span>{format(new Date(activity.created_at), 'MMM d, yyyy h:mm a')}</span>
            </div>
            {activity.description && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{activity.description}</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Badge variant="outline">{activity.activity_type}</Badge>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});
ActivityDetailDialog.displayName = 'ActivityDetailDialog';

// Email Detail Dialog
export const EmailDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  email,
  onCreateTask 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Details
          </DialogTitle>
        </DialogHeader>
        {email && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{email.subject}</h3>
              <Badge variant="outline" className={email.direction === 'outbound' ? 'text-blue-600' : 'text-green-600'}>
                {email.direction === 'outbound' ? 'Sent' : 'Received'}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              {format(new Date(email.created_at), 'PPpp')}
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm whitespace-pre-wrap">{email.body}</p>
            </div>
            <Separator />
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onCreateTask?.({ type: 'email', id: email.id, name: email.subject || 'Email' })}
              data-testid="email-create-task-btn"
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Create Task from Email
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});
EmailDetailDialog.displayName = 'EmailDetailDialog';

// Call Detail Dialog
export const CallDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  call,
  onCreateTask 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" />
            Call Details
          </DialogTitle>
        </DialogHeader>
        {call && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge variant="outline">{call.direction}</Badge>
              <Badge className={call.outcome === 'connected' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                {call.outcome?.replace('_', ' ')}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground">Duration</p>
                <p className="font-medium">{call.duration_minutes || Math.floor((call.duration_seconds || 0) / 60)} min</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-medium">{format(new Date(call.created_at), 'PPp')}</p>
              </div>
            </div>
            {call.notes && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Notes</p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{call.notes}</p>
                </div>
              </div>
            )}
            <Separator />
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onCreateTask?.({ type: 'call', id: call.id, name: `Call - ${call.outcome?.replace('_', ' ')}` })}
              data-testid="call-create-task-btn"
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Create Task from Call
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});
CallDetailDialog.displayName = 'CallDetailDialog';

// Payment Detail Dialog
export const PaymentDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  payment,
  formatCurrency = (v) => `$${(v || 0).toFixed(2)}`
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Details
          </DialogTitle>
        </DialogHeader>
        {payment && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-2xl text-emerald-600">{formatCurrency(payment.amount)}</h3>
              <Badge className={payment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {payment.status}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground">Method</p>
                <p className="font-medium capitalize">{payment.method?.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-medium">{format(new Date(payment.created_at), 'PPp')}</p>
              </div>
            </div>
            {payment.description && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Description</p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm">{payment.description}</p>
                </div>
              </div>
            )}
            {payment.transaction_id && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Transaction ID:</span>
                <code className="bg-slate-100 px-2 py-1 rounded text-xs">{payment.transaction_id}</code>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});
PaymentDetailDialog.displayName = 'PaymentDetailDialog';

// Note Detail Dialog
export const NoteDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  note,
  onUpdate
}) => {
  const [editMode, setEditMode] = useState(false);
  // Initialize form state directly from note prop
  const currentContent = note?.content || '';
  const currentPinned = note?.is_pinned || false;
  const [form, setForm] = useState({ content: currentContent, is_pinned: currentPinned });

  // Reset form when note changes using key technique (handled in parent)
  // Or update form on open/note change
  const noteKey = note?.id;
  useEffect(() => {
    if (note && open) {
      setForm({ content: note.content || '', is_pinned: note.is_pinned || false });
    }
  }, [noteKey, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate?.(note.id, form);
    setEditMode(false);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { onOpenChange(open); if (!open) setEditMode(false); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {editMode ? 'Edit Note' : 'Note Details'}
          </DialogTitle>
        </DialogHeader>
        {note && (
          editMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
                  rows={6}
                  required
                  data-testid="note-edit-content"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="pinned"
                  checked={form.is_pinned}
                  onChange={(e) => setForm(p => ({ ...p, is_pinned: e.target.checked }))}
                />
                <Label htmlFor="pinned">Pin this note</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button type="submit" data-testid="note-save-btn">Save Changes</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{note.author?.full_name || note.author?.email || 'Unknown'}</span>
                <span>•</span>
                <span>{format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}</span>
                {note.is_pinned && <Badge className="bg-amber-100 text-amber-700">Pinned</Badge>}
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{note.content}</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMode(true)} data-testid="note-edit-btn">
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              </DialogFooter>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
});
NoteDetailDialog.displayName = 'NoteDetailDialog';

// Meeting Detail Dialog
export const MeetingDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  meeting,
  onUpdate,
  onDelete
}) => {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    title: '',
    start_time: '',
    end_time: '',
    meeting_link: '',
    status: 'scheduled'
  });

  const meetingKey = meeting?.id;
  useEffect(() => {
    if (meeting && open) {
      setForm({
        title: meeting.title || '',
        start_time: meeting.start_time ? new Date(meeting.start_time).toISOString().slice(0, 16) : '',
        end_time: meeting.end_time ? new Date(meeting.end_time).toISOString().slice(0, 16) : '',
        meeting_link: meeting.meeting_link || '',
        status: meeting.status || 'scheduled'
      });
    }
  }, [meetingKey, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate?.(meeting.id, {
      ...form,
      start_time: new Date(form.start_time).toISOString(),
      end_time: new Date(form.end_time).toISOString()
    });
    setEditMode(false);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { onOpenChange(open); if (!open) setEditMode(false); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            {editMode ? 'Edit Meeting' : 'Meeting Details'}
          </DialogTitle>
        </DialogHeader>
        {meeting && (
          editMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                  required
                  data-testid="meeting-edit-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="datetime-local"
                    value={form.end_time}
                    onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Meeting Link</Label>
                <Input
                  value={form.meeting_link}
                  onChange={(e) => setForm(p => ({ ...p, meeting_link: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button type="submit" data-testid="meeting-save-btn">Save Changes</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">{meeting.title}</h3>
                <Badge className={
                  meeting.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                  meeting.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }>{meeting.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Start</p>
                  <p className="font-medium">{format(new Date(meeting.start_time), 'PPp')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">End</p>
                  <p className="font-medium">{format(new Date(meeting.end_time), 'PPp')}</p>
                </div>
              </div>
              {meeting.meeting_link && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Meeting Link</p>
                  <a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                    {meeting.meeting_link}
                  </a>
                </div>
              )}
              {meeting.attendees?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Users className="h-3 w-3" /> Attendees
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {meeting.attendees.map((a, i) => (
                      <Badge key={i} variant="secondary">{a.name || a.email || a}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter className="flex justify-between">
                <Button variant="destructive" size="sm" onClick={() => onDelete?.(meeting.id)} data-testid="meeting-delete-btn">
                  Delete
                </Button>
                <Button variant="outline" onClick={() => setEditMode(true)} data-testid="meeting-edit-btn">
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              </DialogFooter>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
});
MeetingDetailDialog.displayName = 'MeetingDetailDialog';

// Subscription Detail Dialog
export const SubscriptionDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  subscription,
  onUpdate,
  formatCurrency = (v) => `$${(v || 0).toFixed(2)}`
}) => {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    plan_name: '',
    price: '',
    billing_cycle: 'monthly',
    status: 'active'
  });

  useEffect(() => {
    if (subscription) {
      setForm({
        plan_name: subscription.plan_name || '',
        price: subscription.price?.toString() || '',
        billing_cycle: subscription.billing_cycle || 'monthly',
        status: subscription.status || 'active'
      });
    }
  }, [subscription]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate?.(subscription.id, { ...form, price: parseFloat(form.price) || 0 });
    setEditMode(false);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { onOpenChange(open); if (!open) setEditMode(false); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            {editMode ? 'Edit Subscription' : 'Subscription Details'}
          </DialogTitle>
        </DialogHeader>
        {subscription && (
          editMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Plan Name</Label>
                <Input
                  value={form.plan_name}
                  onChange={(e) => setForm(p => ({ ...p, plan_name: e.target.value }))}
                  required
                  data-testid="subscription-edit-plan"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={form.billing_cycle} onValueChange={(v) => setForm(p => ({ ...p, billing_cycle: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button type="submit" data-testid="subscription-save-btn">Save Changes</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">{subscription.plan_name}</h3>
                <Badge className={
                  subscription.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                  subscription.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }>{subscription.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Price</p>
                  <p className="text-xl font-semibold text-emerald-600">{formatCurrency(subscription.price)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Billing</p>
                  <p className="font-medium capitalize">{subscription.billing_cycle}</p>
                </div>
              </div>
              {subscription.start_date && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Started: {format(new Date(subscription.start_date), 'PPP')}</span>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMode(true)} data-testid="subscription-edit-btn">
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              </DialogFooter>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
});
SubscriptionDetailDialog.displayName = 'SubscriptionDetailDialog';

// Deal Detail Dialog
export const DealDetailDialog = memo(({ 
  open, 
  onOpenChange, 
  deal,
  onUpdate,
  formatCurrency = (v) => `$${(v || 0).toFixed(2)}`
}) => {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    title: '',
    value: '',
    stage: 'prospecting',
    expected_close_date: ''
  });

  useEffect(() => {
    if (deal) {
      setForm({
        title: deal.title || '',
        value: deal.value?.toString() || '0',
        stage: deal.stage || 'prospecting',
        expected_close_date: deal.expected_close_date ? deal.expected_close_date.split('T')[0] : ''
      });
    }
  }, [deal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate?.(deal.id, { ...form, value: parseFloat(form.value) || 0 });
    setEditMode(false);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { onOpenChange(open); if (!open) setEditMode(false); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandshakeIcon className="h-5 w-5" />
            {editMode ? 'Edit Deal' : 'Deal Details'}
          </DialogTitle>
        </DialogHeader>
        {deal && (
          editMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                  required
                  data-testid="deal-edit-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.value}
                    onChange={(e) => setForm(p => ({ ...p, value: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm(p => ({ ...p, stage: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospecting">Prospecting</SelectItem>
                      <SelectItem value="qualification">Qualification</SelectItem>
                      <SelectItem value="proposal">Proposal</SelectItem>
                      <SelectItem value="negotiation">Negotiation</SelectItem>
                      <SelectItem value="closed_won">Closed Won</SelectItem>
                      <SelectItem value="closed_lost">Closed Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Expected Close Date</Label>
                <Input
                  type="date"
                  value={form.expected_close_date}
                  onChange={(e) => setForm(p => ({ ...p, expected_close_date: e.target.value }))}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button type="submit" data-testid="deal-save-btn">Save Changes</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">{deal.title}</h3>
                <Badge className={
                  deal.stage === 'closed_won' ? 'bg-emerald-100 text-emerald-700' :
                  deal.stage === 'closed_lost' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }>{deal.stage?.replace('_', ' ')}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Deal Value</p>
                  <p className="text-xl font-semibold text-emerald-600">{formatCurrency(deal.value)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Expected Close</p>
                  <p className="font-medium">
                    {deal.expected_close_date 
                      ? format(new Date(deal.expected_close_date), 'MMM d, yyyy')
                      : 'Not set'}
                  </p>
                </div>
              </div>
              {deal.line_items?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <div className="border rounded-lg divide-y">
                    {deal.line_items.map((item, idx) => (
                      <div key={idx} className="p-2 flex items-center justify-between text-sm">
                        <span>{item.product_name || 'Product'} x{item.quantity}</span>
                        <span>{formatCurrency(item.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMode(true)} data-testid="deal-edit-btn">
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              </DialogFooter>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
});
DealDetailDialog.displayName = 'DealDetailDialog';

export default {
  ActivityDetailDialog,
  EmailDetailDialog,
  CallDetailDialog,
  PaymentDetailDialog,
  NoteDetailDialog,
  MeetingDetailDialog,
  SubscriptionDetailDialog,
  DealDetailDialog
};
